import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar";
import Footer from "../components/sub-comp/MainFooter";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import cmsApi from "../services/cmsApi";
import { getVideoType } from "../utils/getVideoUrl";
import { FALLBACK_IMAGE_SVG } from "../utils/getImageUrl";

const VideoGallery = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        setLoadError("");
        const result = await cmsApi.getGalleryVideos({ category: selectedCategory === 'all' ? null : selectedCategory });
        
        if (result.success && result.videos) {
          setVideos(result.videos);
        } else {
          setVideos([]);
          setLoadError("No videos available right now.");
        }
      } catch {
        setVideos([]);
        setLoadError("Unable to load videos right now.");
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [selectedCategory]);

  const filteredVideos = selectedCategory === "all" 
    ? videos 
    : videos.filter(video => video.category === selectedCategory);
  const categories = [{ id: "all", name: "All Videos" }].concat(
    [...new Set(videos.map((video) => video.category).filter(Boolean))].map((category) => ({
      id: category,
      name: category,
    }))
  );

  const openVideoModal = (video) => {
    setSelectedVideo(video);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedVideo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedVideo]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="mid-desktop-page">
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-custom-pink to-purple-800 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">
            Video <span className="text-gold">Gallery</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Watch educational content, campus events, and student success stories
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          {!loading && loadError && (
            <p className="text-center text-sm text-red-500 mb-4">{loadError}</p>
          )}
          <div className="flex flex-wrap justify-center gap-2 md:gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  selectedCategory === category.id
                    ? "bg-custom-pink text-white shadow-lg transform scale-105"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-custom-pink"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Video Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-custom-pink border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-600">Loading videos...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredVideos.map((video) => (
              <div
                key={video.id}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:scale-105 cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openVideoModal(video);
                }}
              >
                {/* Thumbnail Container */}
                <div className="relative group">
                  {(() => {
                    const videoInfo = getVideoType(video.videoUrl);
                    const { isLocal, url } = videoInfo;
                    
                    if (isLocal && url) {
                      // Use video element to show first frame as thumbnail
                      return (
                        <video
                          src={url}
                          className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                          muted
                          preload="metadata"
                          onLoadedMetadata={(e) => {
                            // Seek to first frame
                            e.target.currentTime = 0.1;
                          }}
                        />
                      );
                    } else {
                      // Use thumbnail image or fallback
                      return (
                        <img
                          src={video.thumbnail || FALLBACK_IMAGE_SVG}
                          alt={video.title}
                          className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                          onError={(e) => {
                            e.target.src = FALLBACK_IMAGE_SVG;
                          }}
                        />
                      );
                    }
                  })()}
                  
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-10 h-10 text-custom-pink ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>


                  {/* Category Badge */}
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 bg-gold text-white text-xs font-medium rounded-full">
                      {categories.find(cat => cat.id === video.category)?.name}
                    </span>
                  </div>
                </div>

                {/* Video Info */}
                <div className="p-4">
                  <h3 className="font-bold text-lg text-gray-800 mb-2 line-clamp-2 group-hover:text-custom-pink transition-colors duration-300">
                    {video.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {video.description}
                  </p>
                  
                  {video.instructor && (
                    <div className="flex items-center text-sm text-gray-500">
                      <span className="text-xs">{video.instructor}</span>
                    </div>
                  )}
                </div>
              </div>
              ))}
            </div>
          )}

          {/* No Videos Message */}
          {!loading && filteredVideos.length === 0 && (
            <div className="text-center py-20">
              <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No videos found</h3>
              <p className="text-gray-500">Try selecting a different category or check back later for new content.</p>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-custom-pink to-purple-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Have a Great Video to Share?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Submit your educational videos and help others learn from your expertise!
          </p>
          <Link to="/contact" className="inline-block bg-gold text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-yellow-500 transition-colors duration-300 transform hover:scale-105">
            Submit Video
          </Link>
        </div>
      </section>

      {/* Video Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeVideoModal();
            }
          }}
          style={{ zIndex: 9999 }}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-800">{selectedVideo.title}</h3>
              <button
                onClick={closeVideoModal}
                className="text-gray-500 hover:text-gray-700 transition-colors duration-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4">
              <div className="relative w-full aspect-video mb-4 bg-black rounded-lg overflow-hidden">
                {(() => {
                  const videoInfo = getVideoType(selectedVideo.videoUrl);
                  const { isLocal, isYouTube, url } = videoInfo;

                  if (isLocal) {
                    // Use video tag for local files
                    return (
                      <video
                        key={url}
                        src={url}
                        controls
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          console.error('Video load error:', url, e);
                          const errorDiv = e.target.parentElement?.querySelector('.video-error');
                          if (!errorDiv) {
                            const errorMsg = document.createElement('div');
                            errorMsg.className = 'video-error absolute inset-0 flex items-center justify-center text-white bg-black/80';
                            errorMsg.innerHTML = `
                              <div class="text-center p-4">
                                <p class="text-lg font-semibold mb-2">Video not found</p>
                                <p class="text-sm text-gray-400">URL: ${url}</p>
                                <p class="text-xs text-gray-500 mt-2">Please check the video URL in the dashboard</p>
                              </div>
                            `;
                            e.target.parentElement?.appendChild(errorMsg);
                          }
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    );
                  } else if (isYouTube || url.includes('youtube.com') || url.includes('youtu.be')) {
                    // Use iframe for YouTube embeds
                    return (
                      <iframe
                        key={url}
                        src={url}
                        title={selectedVideo.title}
                        className="w-full h-full rounded-lg"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    );
                  } else {
                    // Fallback: try as iframe first, then video tag
                    return (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center text-white p-4">
                          <p className="text-lg font-semibold mb-2">Unable to load video</p>
                          <p className="text-sm text-gray-400 break-all">{url || selectedVideo.videoUrl}</p>
                        </div>
                      </div>
                    );
                  }
                })()}
              </div>
              
              <div className="space-y-3">
                <h4 className="text-lg font-semibold text-gray-800">{selectedVideo.title}</h4>
                <p className="text-gray-600">{selectedVideo.description}</p>
                {selectedVideo.instructor && (
                  <div className="flex items-center text-sm text-gray-500">
                    <span>Instructor: {selectedVideo.instructor}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      </div>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default VideoGallery;
